import React, { useState, useEffect } from 'react';
import { useUser } from 'util/UserContext';
import { Info } from 'lucide-react';
import TextEditor from 'components/TextEditor';
import api from 'config/axiosConfig';
import { Solution } from 'util/constants';
import Modal from 'ui/Modal';
import InputTextArea from 'ui/InputTextArea';
import EntityPrivacy from 'components/Entities/EntityPrivacy';
import { PrivacyLevel } from 'util/userUtils';
import CoauthorSelect, { CoauthorSuggestion } from 'components/Coauthors/CoauthorSelect';
import EntityAvatar from 'components/Entities/EntityAvatar';

interface Props {
  issueId: number;
  onSolutionAdded: (solution: Solution) => void;
  open: boolean;
  onClose: () => void;
}

const CreateSolution: React.FC<Props> = ({ issueId, onSolutionAdded, open, onClose }) => {
  const { currentUser, awaitingUser, setShowAuthModal } = useUser();
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [referenceLink, setReferenceLink] = useState<string>('');
  const [privacy, setPrivacy] = useState<PrivacyLevel>(currentUser?.properties?.privacy_content_default_audience?.value || 'public');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [titleValidationMessage, setTitleValidationMessage] = useState<string>('');
  const [submitEnabled, setSubmitEnabled] = useState<boolean>(false);
  const [coauthors, setCoauthors] = useState<CoauthorSuggestion[]>([]);

  useEffect(() => {
    if (awaitingUser) {
      return;
    }
    if (currentUser && currentUser?.properties?.privacy_content_default_audience) {
      setPrivacy(currentUser?.properties?.privacy_content_default_audience.value);
    } else {
      setPrivacy('public');
    }
  }, [currentUser, awaitingUser]);

  useEffect(() => {
    const isValidSubmit = () => {

      setTitleValidationMessage('');
      setError('');

      // Initial check
      if (!name && !description) {
        return false;
      }

      // Title validation
      if (!name || name.length < 1) {
        setTitleValidationMessage('Title is required.');
        return false;
      }

      // Description validation
      if ((!description || description.length < 1) || description.trim() === '<p></p>') {
        if (!name || name.length < 1) {
          setTitleValidationMessage('Title is required.');
        } else {
          setError('Description is required.');
        }
        return false;
      }

      // Not currently submitting
      if (isSubmitting) {
        return false;
      }

      setTitleValidationMessage('');
      setError('');
      return true;
    };

    setSubmitEnabled(isValidSubmit());
  }, [name, description, isSubmitting]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }

    if (!submitEnabled) {
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      let standardizedLink = referenceLink.trim();
      if (standardizedLink && (!standardizedLink.startsWith('https://') && !standardizedLink.startsWith('http://'))) {
        standardizedLink = standardizedLink.replace(/^(.*?:\/\/)?/, 'https://');
      }

      const solutionData = {
        name: name.trim(),
        description: description.trim(),
        reference_link: standardizedLink,
        user_id: currentUser.id,
        properties: {
          privacy: privacy
        }
      };

      const response = await api.post<Solution>(`/api/solutions/${issueId}`, solutionData);
      const newSolution = response.data;

      await api.post(`/api/votes/solution/${newSolution.id}`, {
        user_id: currentUser.id,
        rel_type: 'user_upvote',
      });

      // Add coauthors if any
      if (coauthors.length > 0) {
        try {
          await api.post(`/api/coauthors/solution/${newSolution.id}/request`, {
            addedCoauthors: coauthors.map(c => ({
              id: c.id,
              type: c.type
            }))
          });
        } catch (err) {
          console.error('Error adding coauthors:', err);
          // Continue even if coauthors couldn't be added
        }
      }

      setSuccess('Solution added successfully!');
      onSolutionAdded(newSolution);
      handleCloseModal();
    } catch (err: any) {
      if (err.response && err.response.status === 401) {
        setShowAuthModal(true);
      } else {
        setError('Failed to add solution. Please try again later.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrivacyChange = (newPrivacy: PrivacyLevel) => {
    setPrivacy(newPrivacy);
  };

  const handleCloseModal = () => {
    onClose();
    setName('');
    setDescription('');
    setReferenceLink('');
    setPrivacy('public');
    setError('');
    setSuccess('');
    setTitleValidationMessage('');
    setCoauthors([]);
  };

  const handleCoauthorSelect = (suggestion: CoauthorSuggestion) => {
    if (!coauthors.some(c => c.id === suggestion.id && c.type === suggestion.type)) {
      setCoauthors([...coauthors, suggestion]);
    }
  };

  const handleCoauthorRemove = (coauthor: CoauthorSuggestion) => {
    setCoauthors(coauthors.filter(c => !(c.id === coauthor.id && c.type === coauthor.type)));
  };

  return (
    <Modal isOpen={open} onClose={handleCloseModal} title="Add solution">
      <div className="flex flex-col h-[calc(100%-44px)] max-h-[calc(100%-44px)] sm:h-auto overflow-y-scroll sm:overflow-y-auto space-y-4 overflow-x-hidden">
        <div className="flex flex-col flex-grow space-y-4">
          <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
            Link a solution to this issue. Solutions are actions that can be or are being taken to address an issue.
          </p>

          <form onSubmit={handleSubmit} className="flex flex-col flex-grow space-y-4">
            {/* Title Field */}
            <div className="mb-0">
              <InputTextArea
                id="solution-name"
                name="solution-name"
                labelText="Title"
                fieldRequired
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={300}
                error={titleValidationMessage}
                validationState={titleValidationMessage ? 'invalid' : !titleValidationMessage && name && name.length > 0 ? 'valid' : 'none'}
              />
            </div>

            {/* Description Field */}
            <div className="mt-0">
              <TextEditor
                value={description}
                onChange={setDescription}
                editorType="entity"
                placeholder="Description"
                fieldRequired
              />
            </div>

            {/* Reference Link Field */}
            <div className="form-control">
              <InputTextArea
                id="solution-reference-link"
                name="solution-reference-link"
                labelText="Reference URL"
                value={referenceLink}
                onChange={(e) => setReferenceLink(e.target.value)}
                spellCheck={false}
              />
            </div>

            {/* Coauthors Field */}
            <div className="form-control">
              <div className="flex flex-col gap-2">
                <CoauthorSelect
                  inputType="add"
                  placeholder="Add collaborators"
                  className="w-full"
                  onSelect={handleCoauthorSelect}
                  excludeIds={coauthors.map(c => c.id)}
                />
                {coauthors.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {coauthors.map((coauthor) => (
                      <EntityAvatar
                        key={`${coauthor.type}-${coauthor.id}`}
                        entity={coauthor as any}
                        entityType={coauthor.type}
                        entityChip={true}
                        showLink={false}
                        showCloseChip={true}
                        onEntityChipClose={() => handleCoauthorRemove(coauthor)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Privacy Settings */}
            <div className="form-control">
              <EntityPrivacy
                privacy={privacy}
                onPrivacyChange={handlePrivacyChange}
                actorType="user"
                editMode={false}
              />
            </div>

            {/* Error and Success Messages */}
            {(error || success) && (
              <div className="flex items-center space-x-2">
                {error && (
                  <div className="flex items-center text-warning-red dark:text-warning-red-dark text-sm">
                    <Info className="mr-1 size-4" />
                    <span>{error}</span>
                  </div>
                )}
                {success && (
                  <div className="flex items-center text-green-600 text-sm">
                    <Info className="mr-1 size-4" />
                    <span>{success}</span>
                  </div>
                )}
              </div>
            )}
          </form>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-grow flex-col pb-8 justify-end place-items-end mb-6 mt-auto space-y-2 sm:space-y-0 sm:space-x-2 sm:flex sm:justify-end sm:flex-row sm:mb-0">
          <button
            type="button"
            onClick={handleCloseModal}
            className="btn-secondary w-full sm:w-fit py-2 px-4"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isSubmitting || !submitEnabled}
            className={`w-full sm:w-fit py-2 px-4 ${
              !submitEnabled ? 'btn-primary-disabled' : 'btn-primary'
            } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CreateSolution;